import "./PropertySelect.css";

import {
  DEFAULT_BLACKLISTED_TRAITS,
  FEATURE_PROPERTY_CATEGORIES,
} from "../../constants";
import {
  selectFeaturesByDateWithAUCData,
  selectFeaturesWithAUCData,
} from "../../selectors/resultMap";
import { useMemo, useState } from "react";

import { Badge } from "reactstrap";
import PropTypes from "prop-types";
import { PropertyName } from "../PropertyName/PropertyName";
import { CfSelect as ReactSelect } from "../CfSelect";
import { components as ReactSelectComponents } from "react-select";
import { selectTraitsIndexedOnTechnicalName } from "../../selectors/traits";
import { useGetPropertyCategory } from "../../hooks/useGetPropertyCategory";
import { useSelector } from "react-redux";

// Order of the groups in this menu only: attributes first, then custom data,
// then traits. The `priority` of the categories (traits first) still orders
// the map legend and the other lists.
const MENU_ORDER = [
  FEATURE_PROPERTY_CATEGORIES.RESERVED,
  FEATURE_PROPERTY_CATEGORIES.DEFAULT,
  FEATURE_PROPERTY_CATEGORIES.RATING,
  FEATURE_PROPERTY_CATEGORIES.IMPORTED,
  FEATURE_PROPERTY_CATEGORIES.HIPHEN,
  FEATURE_PROPERTY_CATEGORIES.INTEGRATIVE,
];

const SearchLabel = ({ data, inputValue }) =>
  data.value ? (
    <PropertyName showIcon technicalName={data.value} highlight={inputValue} />
  ) : (
    data.label
  );

SearchLabel.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }).isRequired,
  inputValue: PropTypes.string,
};

// Inside react-select's own SingleValue: it is the one laid over the input on
// one line; a bare label pushed the input to a second line. No highlight: it
// is hidden as soon as something is typed.
const SingleValueLabel = (props) => (
  <ReactSelectComponents.SingleValue {...props}>
    <SearchLabel data={props.data} />
  </ReactSelectComponents.SingleValue>
);

SingleValueLabel.propTypes = {
  data: PropTypes.object.isRequired,
};

export const PropertySelect = (props) => {
  const {
    onChange,
    value,
    placeholder,
    autoFocus,
    multiDate,
    filterFn,
    optionKeys,
    showUnit,
    caret,
    leadingOption,
    ...rest
  } = props;
  const [focused, setFocused] = useState(autoFocus);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // If multiDate, then options will be generated using featuresByDate
  const features = useSelector(
    multiDate ? selectFeaturesByDateWithAUCData : selectFeaturesWithAUCData
  );

  const getPropertyCategory = useGetPropertyCategory();
  const traitsObject = useSelector(selectTraitsIndexedOnTechnicalName);

  const [propertiesKeys, propertyCategories] = useMemo(() => {
    const propertiesKeys = new Set();
    const propertyCategories = {};

    // An explicit list replaces the keys read on the plots: the map legend
    // offers exactly the attributes of the Results list, experiment and
    // modality included even where no plot carries them.
    if (optionKeys) {
      optionKeys.forEach((key) => {
        propertiesKeys.add(key);
        propertyCategories[key] = getPropertyCategory(key);
      });
      return [propertiesKeys, propertyCategories];
    }

    features.forEach(({ properties, date }) => {
      Object.keys(properties).forEach((key) => {
        // Store categories of keys in object to avoid redundant calls
        if (!propertyCategories[key])
          propertyCategories[key] = getPropertyCategory(key);

        /* If multiDate then we want to concatenate @date to each hiphen trait */
        propertiesKeys.add(
          multiDate &&
            propertyCategories[key] === FEATURE_PROPERTY_CATEGORIES.HIPHEN
            ? `${key}@${date}`
            : key
        );
      });
    });
    return [propertiesKeys, propertyCategories];
  }, [features, getPropertyCategory, multiDate, optionKeys]);

  const options = useMemo(
    () =>
      Array.from(propertiesKeys)
        .filter(
          (key) =>
            !DEFAULT_BLACKLISTED_TRAITS.includes(key.toLowerCase()) &&
            (!filterFn || filterFn(key)) // Optionnal filter property
        )
        .map((key) => {
          const basekey = key.split("@")[0];
          return {
            label: traitsObject[basekey]?.name ?? key,
            value: key,
            // An optionKeys attribute is indexed whole, "@" in its name or not.
            category: propertyCategories[key] ?? propertyCategories[basekey],
          };
        })
        .toSorted(
          (a, b) =>
            MENU_ORDER.indexOf(a.category) - MENU_ORDER.indexOf(b.category) ||
            a.label.localeCompare(b.label)
        ),
    [propertiesKeys, propertyCategories, traitsObject, filterFn]
  );

  const groupedOptions = Object.values(
    options.reduce((acc, option) => {
      const categoryLabel = option.category.label;

      if (!acc[categoryLabel])
        acc[categoryLabel] = { label: categoryLabel, options: [] };

      acc[categoryLabel].options.push({
        label: option.label,
        value: option.value,
      });

      return acc;
    }, {})
  );

  // Category pills at the top of the menu filter which group of options shows,
  // mirroring the ComplexSearch category pills.
  const categories = groupedOptions.map(({ label }) => label);
  const displayedOptions = selectedCategory
    ? groupedOptions.filter(({ label }) => label === selectedCategory)
    : groupedOptions;

  const pickLeading = () => {
    onChange({ ...leadingOption, isLeading: true });
    setFocused(false);
    setSelectedCategory(null);
  };

  const MenuList = (menuListProps) => (
    <ReactSelectComponents.MenuList {...menuListProps}>
      {(leadingOption || categories.length > 1) && (
        <div
          className="property-select-pills d-flex flex-wrap gap-1 px-2 py-1"
          onMouseDown={(e) => e.preventDefault()}
        >
          {leadingOption && (
            <Badge
              pill
              className={`hiphen-badge small clickable ${
                value ? "" : "active"
              }`}
              onClick={pickLeading}
            >
              {leadingOption.label}
            </Badge>
          )}
          {categories.length > 1 &&
            categories.map((category) => (
              <Badge
                pill
                key={category}
                className={`hiphen-badge small clickable ${
                  category === selectedCategory ? "active" : ""
                }`}
                onClick={() =>
                  setSelectedCategory((current) =>
                    current === category ? null : category
                  )
                }
              >
                {category}
              </Badge>
            ))}
        </div>
      )}
      {menuListProps.children}
    </ReactSelectComponents.MenuList>
  );

  return focused ? (
    <ReactSelect
      maxMenuHeight={260}
      {...rest}
      defaultMenuIsOpen
      autoFocus
      styles={{
        ...rest.styles,
        menuList: (base) => ({ ...base, height: base.maxHeight }),
      }}
      options={displayedOptions}
      placeholder={placeholder}
      value={options.find((opt) => opt.value === value)}
      onChange={(value) => {
        onChange(value);
        setFocused(false);
      }}
      onBlur={() => {
        setFocused(false);
        setSelectedCategory(null);
      }}
      components={{
        MenuList,
        Option: ({ innerProps, data, selectProps }) => (
          <div className="p-2 clickable" {...innerProps}>
            <SearchLabel data={data} inputValue={selectProps.inputValue} />
          </div>
        ),
        SingleValue: SingleValueLabel,
      }}
    />
  ) : (
    <Badge
      onClick={() => setFocused(true)}
      className={`hiphen-badge elevated clickable ${props.className || ""}`}
    >
      {value ? (
        <PropertyName showIcon showUnit={showUnit} technicalName={value} />
      ) : (
        <span className="font-style-italic">{placeholder}</span>
      )}
      {caret && (
        <i className="fa-solid fa-chevron-down property-select-caret" />
      )}
    </Badge>
  );
};

PropertySelect.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
  className: PropTypes.string,
  multiDate: PropTypes.bool,
  filterFn: PropTypes.func,
  // Keys to offer instead of the properties read on the plots.
  optionKeys: PropTypes.arrayOf(PropTypes.string),
  // Closed badge only: the unit after the name, a chevron that reads as a picker.
  showUnit: PropTypes.bool,
  caret: PropTypes.bool,
  leadingOption: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  // + ReactSelect props
};
